import type { MouseEvent, ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '@/shared/lib/cn';
import { Tooltip } from './Tooltip';

export interface NavItem {
  /** Adres docelowy. Pomijamy go dla pozycji, ktore tylko wywoluja akcje. */
  to?: string;
  label: string;
  icon: ReactNode;
  /** Dopasowanie tylko do dokladnej sciezki (dla trasy głównej „/”). */
  end?: boolean;
  /** Liczba przy pozycji, np. nieprzeczytane wiadomosci. */
  badge?: number;
  /**
   * Akcja po klinieciu. Bez `to` pozycja renderuje sie jako przycisk zamiast
   * odnosnika (obszary bez wlasnego widoku); razem z `to` odpala sie przed
   * przejsciem na trase — `preventDefault()` na zdarzeniu je wstrzymuje.
   */
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  /**
   * Druga warstwa nawigacji — np. „Przydzial kortow” pod „Infrastruktura”
   * albo lista aktywnych turniejow pod „Na skroty”.
   *
   * Pozycja z `children`, ale bez `to` i bez `onClick`, jest samym naglowkiem
   * sekcji: renderuje sie jako napis, a nie jako martwy przycisk.
   */
  children?: readonly NavItem[];
  /** Napis zamiast pustej listy podpozycji, np. „Brak aktywnych turniejow”. */
  emptyLabel?: string;
}

export interface SidebarProps {
  items: readonly NavItem[];
  /** Zawartosc nad nawigacja — zwykle logo. */
  header?: ReactNode;
  /** Zawartosc przyklejona do dolu — zwykle wylogowanie albo karta uzytkownika. */
  footer?: ReactNode;
  /**
   * `labels` — ikona obok etykiety (uklad z wiekszosci makiet),
   * `rail` — ikona nad etykieta, aktywna pozycja jako pelny kwadrat marki
   *          (uklad z `drabinka.png`),
   * `icons` — sama ikona, etykieta w dymku po najechaniu. Uklad na czas,
   *           gdy obok stoi druga kolumna nawigacji (panel turniejow) i pelne
   *           etykiety zabralyby jej szerokosc.
   */
  variant?: 'labels' | 'rail' | 'icons';
  className?: string;
}

/**
 * Pionowa nawigacja glowna aplikacji.
 *
 * Stan aktywnosci bierzemy z `NavLink` routera, wiec podswietlenie zawsze zgadza
 * sie z adresem — takze po wejsciu z zakladki albo przycisku „wstecz”.
 */
export function Sidebar({ items, header, footer, variant = 'labels', className }: SidebarProps) {
  const isRail = variant === 'rail';
  const isIcons = variant === 'icons';
  /** Warianty bez widocznej etykiety obok ikony — kwadratowe kafle, wysrodkowane. */
  const isNarrow = isRail || isIcons;

  /**
   * Podpozycje maja sens tylko tam, gdzie widac etykiety. W `rail` i `icons`
   * zostaja same ikony, a te dla turnieju czy „Przydzialu kortow” nic by nie
   * mowily — w tych wariantach druga warstwa po prostu sie nie rozwija.
   */
  const showChildren = variant === 'labels';

  /** Wyglad pozycji — wspolny dla odnosnika i przycisku akcji. */
  const itemClass = (isActive: boolean, nested = false) =>
    cn(
      // Ikony z ciensza kreska niz domyslne 2px lucide — menu ma byc delikatne.
      'relative flex items-center font-medium transition-colors duration-150 [&_svg]:stroke-[1.5]',
      isRail && 'w-full flex-col justify-center gap-1.5 rounded-2xl px-2 py-3 text-[0.6875rem]',
      isIcons && 'size-11 justify-center rounded-xl',
      variant === 'labels' && 'w-full gap-3 rounded-lg px-3 py-2.5 text-sm',
      // Podpozycja jest wezsza i mniejsza — ikona schodzi do rozmiaru tekstu,
      // zeby druga warstwa czytala sie jako podrzedna, a nie rownorzedna.
      nested ? 'gap-2.5 py-2 pl-3 text-[0.8125rem] [&_svg]:size-4' : '[&_svg]:size-5',
      isActive
        ? isNarrow
          ? 'bg-brand text-fg-on-brand font-bold'
          : 'bg-brand-subtle text-fg-strong font-semibold'
        : 'text-fg-muted hover:bg-surface-hover hover:text-fg',
    );

  const itemBody = (item: NavItem, nested = false) => (
    <>
      {item.icon}
      {/* W trybie `icons` etykieta zwija sie do zerowej szerokosci zamiast
          znikac — dzieki temu przejscie jest plynne, a tekst zostaje w drzewie
          dostepnosci i dalej stanowi nazwe pozycji (widzacy dostaje go w dymku). */}
      <span
        className={cn(
          isRail && 'text-center leading-tight',
          // Nazwa turnieju bywa dluzsza niz pasek — przycinamy ja zamiast
          // rozpychac kolumne.
          nested && 'min-w-0 truncate',
          isIcons &&
            'max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-[max-width,opacity] duration-200 ease-out motion-reduce:transition-none',
        )}
      >
        {item.label}
      </span>

      {item.badge !== undefined && item.badge > 0 && (
        <span
          className={cn(
            'bg-danger-500 grid min-w-4 place-items-center rounded-full px-1',
            'text-[0.625rem] font-bold text-white',
            isRail && 'absolute top-2 right-3',
            isIcons && 'absolute top-0.5 right-0.5',
            variant === 'labels' && 'ml-auto',
          )}
        >
          {item.badge > 9 ? '9+' : item.badge}
        </span>
      )}
    </>
  );

  /** Owija pozycje w dymek z etykieta — tylko tam, gdzie etykiety nie widac. */
  const withTooltip = (item: NavItem, control: ReactNode) =>
    isIcons ? (
      <Tooltip content={item.label} side="right-bottom">
        {control as Parameters<typeof Tooltip>[0]['children']}
      </Tooltip>
    ) : (
      control
    );

  /**
   * Sterowanie pozycji: odnosnik, przycisk akcji albo — dla pozycji z samymi
   * podpozycjami — naglowek sekcji. Ten ostatni celowo nie jest przyciskiem:
   * nie ma dokad prowadzic, a martwy przycisk lapalby fokus na darmo.
   */
  const renderControl = (item: NavItem, nested = false) => {
    if (item.to) {
      return (
        <NavLink
          to={item.to}
          {...(item.end !== undefined ? { end: item.end } : {})}
          {...(item.onClick ? { onClick: item.onClick } : {})}
          className={({ isActive }) => itemClass(isActive, nested)}
        >
          {itemBody(item, nested)}
        </NavLink>
      );
    }

    if (item.onClick) {
      return (
        <button type="button" onClick={item.onClick} className={itemClass(false, nested)}>
          {itemBody(item, nested)}
        </button>
      );
    }

    return (
      <span
        className={cn(
          'text-fg-subtle flex items-center gap-2.5 px-3 pt-4 pb-1',
          'text-[0.6875rem] font-bold tracking-[0.08em] uppercase',
          '[&_svg]:size-4',
        )}
      >
        {item.icon}
        {item.label}
      </span>
    );
  };

  return (
    <div
      className={cn(
        'bg-surface border-border flex h-full flex-col border-r',
        'transition-[width] duration-200 ease-out motion-reduce:transition-none',
        isRail && 'w-[6.5rem]',
        isIcons && 'w-[5.25rem]',
        // Szerokosc kompensuje wieksze wciecie od krawedzi (px-5 w nav) —
        // dluzsze etykiety („Zaplanowane mecze”) maja sie miescic bez przycinania.
        variant === 'labels' && 'w-[16.5rem]',
        className,
      )}
    >
      {header && (
        // W ukladzie z etykietami logo dostaje to samo wciecie co tresc pozycji
        // menu (px-5 kontenera + px-3 wnetrza pozycji) — lewa krawedz logo
        // rownia sie z ikonami.
        <div className={cn('shrink-0', isNarrow ? 'flex justify-center px-3 py-5' : 'px-8 py-5')}>
          {header}
        </div>
      )}

      {/* `overflow-x-hidden` jest tu istotne: w trakcie animacji zwezania tresc
          bywa przez chwile szersza od paska i bez tego wyskakiwalby poziomy
          pasek przewijania. */}
      <nav
        aria-label="Nawigacja główna"
        className={cn(
          'min-h-0 flex-1 overflow-x-hidden overflow-y-auto py-1',
          // Wieksze wciecie od krawedzi w ukladzie z etykietami — pozycje nie
          // klei sie do lewej sciany paska.
          isNarrow ? 'px-3' : 'px-5',
        )}
      >
        <ul className={cn('flex flex-col', isNarrow ? 'gap-2' : 'gap-0.5', isIcons && 'items-center')}>
          {items.map((item) => {
            // Sama obecnosc `children` decyduje o drugiej warstwie — pusta lista
            // to nadal sekcja (pokaze `emptyLabel`), a brak pola to zwykla pozycja.
            const nested = showChildren && item.children !== undefined;

            return (
              <li key={item.to ?? item.label} className={cn(isIcons && 'flex justify-center')}>
                {withTooltip(item, renderControl(item))}

                {nested && (
                  <ul className="flex flex-col gap-0.5 pb-1">
                    {item.children?.map((child) => (
                      <li key={child.to ?? child.label}>{renderControl(child, true)}</li>
                    ))}

                    {item.children?.length === 0 && item.emptyLabel && (
                      <li className="px-3 py-1.5">
                        <span className="text-fg-subtle text-xs">{item.emptyLabel}</span>
                      </li>
                    )}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      {footer && (
        <div className={cn('border-border shrink-0 border-t', isNarrow ? 'p-3' : 'px-5 py-3')}>
          {footer}
        </div>
      )}
    </div>
  );
}
